import { useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Maximize2, GripVertical } from "lucide-react";

import type { Panneau as PanneauModel, PanneauDetail } from "../api/types";
import { PanneauContainer } from "./PanneauContainer";

interface Props {
  conv: string;
  summary: PanneauModel;
  onExpand: (panneauId: string) => void;
  onArchive?: (panneau: PanneauDetail) => void;
}

/**
 * Wraps a PanneauContainer in @dnd-kit's sortable behaviour and adds
 * a small drag handle + expand button overlay in the top-right corner
 * (without overlapping the panneau's own close button — they stack).
 */
export function SortablePanneau({ conv, summary, onExpand, onArchive }: Props) {
  const [hover, setHover] = useState(false);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: summary.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : "auto",
  } as React.CSSProperties;

  return (
    <div
      ref={setNodeRef}
      style={style}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="relative"
    >
      {/* Hover-only drag handle and expand button — top-left so they don't
          collide with the panneau's existing top-right close action. */}
      {hover && !isDragging && (
        <div className="absolute -top-2 -left-2 z-20 flex gap-1">
          <button
            type="button"
            {...attributes}
            {...listeners}
            title="Glisser pour réorganiser"
            aria-label="Glisser pour réorganiser le panneau"
            className="h-7 w-7 rounded-full bg-white dark:bg-surface-dark border border-black/10 dark:border-white/10 shadow-sm flex items-center justify-center text-muted dark:text-muted-dark hover:text-ink dark:hover:text-ink-dark cursor-grab active:cursor-grabbing"
          >
            <GripVertical className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onExpand(summary.id)}
            title="Déployer en plein écran"
            aria-label="Déployer le panneau"
            className="h-7 w-7 rounded-full bg-white dark:bg-surface-dark border border-black/10 dark:border-white/10 shadow-sm flex items-center justify-center text-muted dark:text-muted-dark hover:text-ink dark:hover:text-ink-dark"
          >
            <Maximize2 className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      <PanneauContainer conv={conv} summary={summary} onArchive={onArchive} />
    </div>
  );
}
